import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, catchError, EMPTY, lastValueFrom, Observable, Subject, Subscription, switchMap } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { ChatApiService, ChatConnectionError } from '../api/chat/chat.api.service';
import {
  ChatMessage,
  ChatState,
  ChatEvent,
  ChatEventType,
  ChatMessageEvent,
  ChatErrorEvent,
  ChatError,
  ConversationContextUsage,
  ConversationTokenUsageResponse,
} from '../types/Chatbot';

const SESSION_STORAGE_KEY = 'vev-iq-chat-messages';
const CONTEXT_WARNING_THRESHOLD = 0.6;
const CONTEXT_CRITICAL_THRESHOLD = 0.9;

const ERROR_CODE_TRANSLATION_MAP: Record<string, string> = {
  timeout: 'chatbot.error_timeout',
  rate_limit: 'chatbot.error_rate_limit',
  provider_error: 'chatbot.error_provider',
  auth_error: 'chatbot.error_auth',
  stream_error: 'chatbot.error_stream',
  internal_error: 'chatbot.error_internal',
  validation_error: 'chatbot.error_validation',
};

@Injectable({
  providedIn: 'root',
})
export class ChatService implements OnDestroy {
  private readonly stateSubject = new BehaviorSubject<ChatState>({
    messages: [],
    isConnected: false,
    isLoading: false,
    error: null,
    persistenceError: null,
    hasReachedContextLimit: false,
    contextUsage: null,
  });

  private currentMessageId: string | null = null;
  private streamSubscription: Subscription | null = null;
  private isProcessingMessage = false;
  private readonly tokenUsageRefresh$ = new Subject<void>();
  private readonly tokenUsageSubscription: Subscription;

  public constructor(private readonly chatApiService: ChatApiService) {
    // switchMap drops a stale in-flight usage request when a newer refresh starts
    // (e.g. a refresh triggered before clearMessages() must not re-latch the limit on the cleared chat).
    this.tokenUsageSubscription = this.tokenUsageRefresh$
      .pipe(
        switchMap(() =>
          this.chatApiService.getConversationTokenUsage().pipe(
            // Context usage should never block chat usage if the informational endpoint is unavailable.
            catchError(() => EMPTY),
          ),
        ),
      )
      .subscribe((usage) => this.applyConversationTokenUsage(usage));
    this.loadMessagesFromSession();
  }

  public get state$(): Observable<ChatState> {
    return this.stateSubject.asObservable();
  }

  public get currentState(): ChatState {
    return this.stateSubject.getValue();
  }

  public sendMessage(content: string): void {
    if (!content.trim() || this.isProcessingMessage || this.currentState.hasReachedContextLimit) {
      return;
    }

    this.isProcessingMessage = true;

    // Add user message
    const userMessage: ChatMessage = {
      id: uuidv4(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    };

    this.updateState({
      messages: [...this.currentState.messages, userMessage],
      isLoading: true,
      error: null,
    });

    // Create placeholder for assistant response
    this.currentMessageId = uuidv4();
    const assistantMessage: ChatMessage = {
      id: this.currentMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true,
    };

    this.updateState({
      messages: [...this.currentState.messages, assistantMessage],
    });

    this.saveMessagesToSession();

    // Connect to SSE
    this.streamSubscription = this.chatApiService.connect(content).subscribe({
      next: (event: ChatEvent) => this.handleEvent(event),
      error: (error) => this.handleError(error),
      complete: () => this.handleComplete(),
    });
  }

  public async clearMessages(): Promise<void> {
    this.disconnect();
    const contextWindow = this.currentState.contextUsage?.contextWindow;
    await lastValueFrom(this.chatApiService.deleteConversation());
    this.updateState({
      messages: [],
      error: null,
      hasReachedContextLimit: false,
      contextUsage: contextWindow
        ? this.normalizeConversationTokenUsage({
            scope: 'conversation_history',
            contextWindow,
            effectiveInputBudget: contextWindow,
            estimatedPromptTokens: 0,
            remainingPercentage: 100,
          })
        : null,
    });
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    this.refreshConversationTokenUsage();
  }

  public clearPersistenceError(): void {
    this.updateState({ persistenceError: null });
  }

  public disconnect(): void {
    if (this.streamSubscription) {
      this.streamSubscription.unsubscribe();
      this.streamSubscription = null;
    }
    this.chatApiService.disconnect();
    this.currentMessageId = null;
    this.isProcessingMessage = false;
    this.updateState({ isConnected: false, isLoading: false });
  }

  public reportProblem(details?: string): Observable<void> {
    return this.chatApiService.reportError(details);
  }

  public refreshConversationTokenUsage(): void {
    this.tokenUsageRefresh$.next();
  }

  public ngOnDestroy(): void {
    this.disconnect();
    this.tokenUsageSubscription.unsubscribe();
    this.tokenUsageRefresh$.complete();
  }

  private handleEvent(event: ChatEvent): void {
    switch (event.type) {
      case ChatEventType.Connected:
        this.updateState({ isConnected: true });
        break;

      case ChatEventType.Message:
        this.handleMessageEvent(event.data as ChatMessageEvent);
        break;

      case ChatEventType.Complete:
        this.handleCompleteEvent();
        break;

      case ChatEventType.Error:
        this.handleErrorEvent(event.data as ChatErrorEvent);
        break;

      case ChatEventType.Ping:
        // Keep-alive, no action needed
        break;
    }
  }

  private handleMessageEvent(data: ChatMessageEvent): void {
    const isContextLimitNotice = this.isContextLimitMessage(data);
    const messages = this.currentState.messages.map((msg) => {
      if (msg.id === this.currentMessageId) {
        return {
          ...msg,
          content: msg.content + data.delta,
          isContextLimitNotice: msg.isContextLimitNotice || isContextLimitNotice || undefined,
        };
      }
      return msg;
    });

    this.updateState({
      messages,
      hasReachedContextLimit: this.currentState.hasReachedContextLimit || isContextLimitNotice,
    });
    this.saveMessagesToSession();
  }

  private handleCompleteEvent(): void {
    const messages = this.currentState.messages.map((msg) => {
      if (msg.id === this.currentMessageId) {
        return {
          ...msg,
          isStreaming: false,
        };
      }
      return msg;
    });

    this.updateState({
      messages,
      isLoading: false,
    });

    this.currentMessageId = null;
    this.isProcessingMessage = false;
    this.saveMessagesToSession();
    this.refreshConversationTokenUsage();
  }

  private handleErrorEvent(data: ChatErrorEvent): void {
    // Stop streaming and remove empty assistant message on error
    const messages = this.currentMessageId
      ? this.currentState.messages
          .filter((msg) => msg.id !== this.currentMessageId || msg.content.length > 0)
          .map((msg) => {
            if (msg.id === this.currentMessageId) {
              return { ...msg, isStreaming: false };
            }
            return msg;
          })
      : this.currentState.messages;

    const hasReachedContextLimit = this.currentState.hasReachedContextLimit || this.isContextLimitError(data);
    const error: ChatError = {
      message: this.getErrorTranslationKey(data),
      code: data.code,
    };

    this.updateState({
      messages,
      error,
      isLoading: false,
      hasReachedContextLimit,
    });

    this.currentMessageId = null;
    this.isProcessingMessage = false;
    this.saveMessagesToSession();
    this.refreshConversationTokenUsage();
  }

  private handleError(error: unknown): void {
    // Stop streaming on connection error
    const messages = this.currentMessageId
      ? this.currentState.messages
          .filter((msg) => msg.id !== this.currentMessageId || msg.content.length > 0)
          .map((msg) => {
            if (msg.id === this.currentMessageId) {
              return { ...msg, isStreaming: false };
            }
            return msg;
          })
      : this.currentState.messages;

    // The gateway answers 429 when the user sends more than 20 messages per minute.
    const chatError: ChatError = {
      message:
        error instanceof ChatConnectionError && error.status === 429
          ? 'chatbot.error_too_many_requests'
          : 'chatbot.error_connection',
    };

    this.updateState({
      messages,
      error: chatError,
      isLoading: false,
      isConnected: false,
    });

    this.currentMessageId = null;
    this.isProcessingMessage = false;
    this.saveMessagesToSession();
  }

  private handleComplete(): void {
    this.updateState({ isConnected: false });
  }

  private getErrorTranslationKey(data: ChatErrorEvent): string {
    if (this.isContextLimitError(data)) {
      return 'chatbot.context_limit_reached';
    }

    if (!data.code) {
      return 'chatbot.error_generic';
    }

    return ERROR_CODE_TRANSLATION_MAP[data.code] ?? 'chatbot.error_generic';
  }

  private isContextLimitError(data: ChatErrorEvent): boolean {
    return data.contextLimitReached === true;
  }

  private isContextLimitMessage(data: ChatMessageEvent): boolean {
    return data.contextLimitReached === true;
  }

  private applyConversationTokenUsage(usage: ConversationTokenUsageResponse): void {
    const contextUsage = this.normalizeConversationTokenUsage(usage);
    if (!contextUsage) {
      return;
    }

    this.updateState({
      contextUsage,
      hasReachedContextLimit:
        this.currentState.hasReachedContextLimit ||
        contextUsage.usageRatio >= 1 ||
        contextUsage.remainingPercentage === 0,
    });
  }

  private normalizeConversationTokenUsage(usage: ConversationTokenUsageResponse): ConversationContextUsage | null {
    const contextWindow = Math.max(0, usage.contextWindow);
    if (contextWindow === 0) {
      return null;
    }

    const effectiveInputBudget = Math.max(1, usage.effectiveInputBudget);
    const estimatedPromptTokens = Math.max(0, usage.estimatedPromptTokens);
    const usageRatio = Math.min(estimatedPromptTokens / effectiveInputBudget, 1);

    return {
      ...usage,
      contextWindow,
      effectiveInputBudget,
      estimatedPromptTokens,
      usageRatio,
      usagePercentage: Math.round(usageRatio * 100),
      isNearLimit: usageRatio >= CONTEXT_WARNING_THRESHOLD,
      isCritical: usageRatio >= CONTEXT_CRITICAL_THRESHOLD,
    };
  }

  private updateState(partial: Partial<ChatState>): void {
    const newState = {
      ...this.currentState,
      ...partial,
    };

    this.stateSubject.next(newState);
  }

  /**
   * Saves messages to sessionStorage.
   * Note: ChatMessage.timestamp is a Date at runtime but serialized as ISO string for storage.
   * The loadMessagesFromSession method handles deserialization back to Date objects.
   */
  private saveMessagesToSession(): void {
    try {
      const messages = this.currentState.messages.map((msg) => ({
        ...msg,
        timestamp: msg.timestamp.toISOString(),
      }));
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(messages));
    } catch (e) {
      console.error('Failed to save messages to session storage:', e);
    }
  }

  /**
   * Loads messages from sessionStorage.
   * Deserializes ISO string timestamps back to Date objects.
   */
  private loadMessagesFromSession(): void {
    try {
      const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (!Array.isArray(parsed)) {
          return;
        }
        const messages: ChatMessage[] = parsed
          .filter(this.isValidStoredMessage)
          .filter((msg) => msg.content?.length > 0) // Remove empty streaming messages
          .map((msg) => ({
            id: msg.id,
            role: msg.role,
            content: msg.content,
            timestamp: new Date(msg.timestamp),
            isStreaming: false, // Reset streaming state on reload
            isContextLimitNotice: msg.isContextLimitNotice === true ? true : undefined,
          }));
        this.updateState({
          messages,
          // Rely on the persisted flag rather than re-parsing message text, so an assistant answer that
          // merely mentions starting a new conversation cannot falsely lock the input after a reload.
          hasReachedContextLimit: messages.some((msg) => msg.isContextLimitNotice),
        });
      }
    } catch (e) {
      console.error('Failed to load messages from session storage:', e);
      const error = e as Error;
      if (error instanceof SyntaxError) {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
      }
      this.updateState({ persistenceError: 'chatbot.persistence_error' });
    }
  }

  private isValidStoredMessage(msg: unknown): msg is {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: string;
    isContextLimitNotice?: boolean;
  } {
    return (
      typeof msg === 'object' &&
      msg !== null &&
      typeof (msg as Record<string, unknown>).id === 'string' &&
      ((msg as Record<string, unknown>).role === 'user' || (msg as Record<string, unknown>).role === 'assistant') &&
      typeof (msg as Record<string, unknown>).content === 'string' &&
      typeof (msg as Record<string, unknown>).timestamp === 'string'
    );
  }
}
